import {PageStandard} from "../utils/PageStandard";
import {
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Drawer,
    FormControl,
    FormLabel,
    IconButton,
    Input,
    Sheet,
    Stack,
    Typography
} from "@mui/joy";
import {Fragment, useEffect, useMemo, useState} from "react";
import {UserModel} from "../utils/Model";
import {deleteUser, editUser, getUsers} from "../utils/ViewModel";
import {
    ArrowDownward,
    ArrowUpward,
    DeleteForever,
    Edit,
    FileDownload,
    Lock,
    Search,
    SwapVert,
} from "@mui/icons-material";
import {downloadCSV, hasChanges} from "../utils/Utils";
import moment from "moment";

type SortKey = "name" | "email" | "role"
type SortDir = "asc" | "desc"

const GRID_TEMPLATE_COLUMNS = "minmax(200px, 1.3fr) minmax(220px, 1.4fr) minmax(120px, 0.8fr) minmax(180px, 1fr) minmax(150px, 1fr) minmax(96px, 0.7fr)"

const HEADER_CELL_SX = {
    px: 1.5,
    py: 1,
    fontWeight: "lg",
    fontSize: "sm",
    bgcolor: "background.level2",
    borderBottom: "1px solid",
    borderColor: "divider",
    display: "flex",
    alignItems: "center",
    gap: 0.5,
} as const

const CELL_SX = {
    px: 1.5,
    py: 1,
    display: "flex",
    alignItems: "center",
    borderBottom: "1px solid",
    borderColor: "divider",
} as const

export function Utenti() {

    const [utenti, setUtenti] = useState<UserModel[]>([])
    const [loading, setLoading] = useState(true)
    const [search, setSearch] = useState("")
    const [sortKey, setSortKey] = useState<SortKey>("name")
    const [sortDir, setSortDir] = useState<SortDir>("asc")

    useEffect(() => {
        loadData()
    }, [])

    function loadData() {
        getUsers().then(data => {
            setUtenti(data);
            setLoading(false)
        });
    }

    function toggleSort(key: SortKey) {
        if (key === sortKey) {
            setSortDir(dir => dir === "asc" ? "desc" : "asc")
        } else {
            setSortKey(key)
            setSortDir("asc")
        }
    }

    const searchLower = search.trim().toLowerCase()

    const filteredUtenti = useMemo(() => {
        const filtered = utenti.filter(u => {
            if (searchLower === "") return true
            const haystack = `${u.firstName ?? ""} ${u.lastName ?? ""} ${u.email}`.toLowerCase()
            return haystack.includes(searchLower)
        })

        const sorted = [...filtered].sort((a, b) => {
            let cmp = 0
            if (sortKey === "name") {
                cmp = `${a.firstName ?? ""} ${a.lastName ?? ""}`.localeCompare(`${b.firstName ?? ""} ${b.lastName ?? ""}`)
            } else if (sortKey === "email") {
                cmp = a.email.localeCompare(b.email)
            } else {
                cmp = Number(b.isAdmin) - Number(a.isAdmin)
            }
            return sortDir === "asc" ? cmp : -cmp
        })

        return sorted
    }, [utenti, searchLower, sortKey, sortDir])

    const stats = useMemo(() => ({
        total: utenti.length,
        admin: utenti.filter(u => u.isAdmin).length,
        marketing: utenti.filter(u => u.marketing).length,
    }), [utenti])

    return (
        <PageStandard title="Utenti" sidebarVisible={true} headerEnd={
            <Stack direction="row" spacing={2}>
                <Button startDecorator={<FileDownload/>}
                        onClick={() => {
                            downloadCSV<UserModel>({
                                data: utenti,
                                fields: ["firstName", "lastName", "email"],
                                filename: `oasi-utenti-${moment().format("YYYY-MM-DD")}.csv`,
                            })
                        }}
                >
                    Esporta tutti in CSV
                </Button>
                <Button startDecorator={<FileDownload/>}
                        onClick={() => {
                            downloadCSV<UserModel>({
                                data: utenti,
                                fields: ["firstName", "lastName", "email"],
                                filename: `oasi-utenti-marketing-${moment().format("YYYY-MM-DD")}.csv`,
                                filter: u => u.marketing,
                            })
                        }}
                >
                    Esporta solo marketing in CSV
                </Button>
            </Stack>
        }>
            <Stack direction="row" spacing={2} alignItems="center" sx={{mb: 2}} flexWrap="wrap">
                <Input
                    placeholder="Cerca per nome, cognome o email..."
                    startDecorator={<Search/>}
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    sx={{minWidth: 280, flexGrow: 1, maxWidth: 420}}
                />
                <Chip size="sm" variant="soft" color="neutral">{stats.total} utenti</Chip>
                <Chip size="sm" variant="soft" color="danger">{stats.admin} admin</Chip>
                <Chip size="sm" variant="soft" color="success">{stats.marketing} consenso marketing</Chip>
            </Stack>

            {
                loading &&
                <Stack alignItems="center" justifyContent="center" sx={{py: 6}}>
                    <CircularProgress/>
                </Stack>
            }

            {
                !loading && filteredUtenti.length === 0 &&
                <Typography level="body-md" sx={{textAlign: "center", py: 4}}>
                    Nessun utente trovato{searchLower !== "" ? ` per "${search}"` : ""}.
                </Typography>
            }

            {
                !loading && filteredUtenti.length > 0 &&
                <Box sx={{overflowX: "auto", pb: 5}}>
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: GRID_TEMPLATE_COLUMNS,
                            minWidth: 1000,
                            border: "1px solid",
                            borderColor: "divider",
                            borderRadius: "md",
                            overflow: "hidden",
                        }}
                    >
                        <SortableHeaderCell label="Nome" active={sortKey === "name"} dir={sortDir}
                                             onClick={() => toggleSort("name")}/>
                        <SortableHeaderCell label="Email" active={sortKey === "email"} dir={sortDir}
                                             onClick={() => toggleSort("email")}/>
                        <SortableHeaderCell label="Ruolo" active={sortKey === "role"} dir={sortDir}
                                             onClick={() => toggleSort("role")}/>
                        <Box sx={HEADER_CELL_SX}>Consensi</Box>
                        <Box sx={HEADER_CELL_SX}>UID</Box>
                        <Box sx={{...HEADER_CELL_SX, justifyContent: "center"}}>Azioni</Box>

                        {filteredUtenti.map((u, idx) => (
                            <RowUtente key={u.id} data={u} loadData={loadData} zebra={idx % 2 === 1}/>
                        ))}
                    </Box>
                </Box>
            }
        </PageStandard>
    )
}

function SortableHeaderCell(props: { label: string, active: boolean, dir: SortDir, onClick: () => void }) {
    return (
        <Box
            sx={{...HEADER_CELL_SX, cursor: "pointer", userSelect: "none"}}
            onClick={props.onClick}
        >
            {props.label}
            {
                props.active ?
                    (props.dir === "asc" ? <ArrowUpward sx={{fontSize: 16}}/> : <ArrowDownward sx={{fontSize: 16}}/>)
                    :
                    <SwapVert sx={{fontSize: 16, opacity: 0.35}}/>
            }
        </Box>
    )
}

function RowUtente(props: { data: UserModel, loadData: () => void, zebra: boolean }) {
    const defaultData = props.data;
    const loadData = props.loadData;

    const [data, setData] = useState<UserModel>(props.data);
    const [modalEdit, setModalEdit] = useState(false)

    const zebraSx = props.zebra ? {bgcolor: "background.level1"} : undefined

    function handleChange<T extends keyof UserModel>(
        key: T,
        value: UserModel[T]
    ) {
        setData((old) => ({...old, [key]: value}));
    }

    const handleSubmit = () => {
        editUser(data)
            .then(() => {
                setModalEdit(false);
                loadData();
            })
    };

    return (
        <Fragment>
            <Box sx={{...CELL_SX, ...zebraSx}}>
                <Typography level="body-sm" fontWeight="lg">
                    {defaultData.firstName} {defaultData.lastName}
                </Typography>
            </Box>
            <Box sx={{...CELL_SX, ...zebraSx}}>
                <Typography level="body-sm">{defaultData.email}</Typography>
            </Box>
            <Box sx={{...CELL_SX, ...zebraSx}}>
                {
                    defaultData.isAdmin ?
                        <Chip size="sm" variant="soft" color="danger" sx={{fontWeight: "bold"}}>Admin</Chip>
                        :
                        <Chip size="sm" variant="soft" color="neutral">Utente</Chip>
                }
            </Box>
            <Box sx={{...CELL_SX, ...zebraSx, gap: 0.5}}>
                <Chip size="sm" variant={defaultData.terms ? "soft" : "outlined"}
                      color={defaultData.terms ? "success" : "neutral"}>
                    Termini
                </Chip>
                <Chip size="sm" variant={defaultData.marketing ? "soft" : "outlined"}
                      color={defaultData.marketing ? "success" : "neutral"}>
                    Marketing
                </Chip>
            </Box>
            <Box sx={{...CELL_SX, ...zebraSx}}>
                <Typography level="body-xs" sx={{fontFamily: "monospace"}} noWrap>
                    {defaultData.id}
                </Typography>
            </Box>
            <Box sx={{...CELL_SX, ...zebraSx, justifyContent: "center", gap: 1}}>
                <IconButton color="primary" variant="outlined" size="sm"
                            onClick={() => setModalEdit(true)}
                >
                    <Edit fontSize="small"/>
                </IconButton>
                <IconButton color="danger" variant="outlined" size="sm"
                            onClick={() => {
                                if (window.confirm(`Eliminare definitivamente l'utente ${defaultData.email}?`)) {
                                    deleteUser(defaultData.id)
                                        .then(() => loadData())
                                }
                            }}
                >
                    <DeleteForever fontSize="small"/>
                </IconButton>
            </Box>

            <Drawer open={modalEdit} onClose={() => cancelModal()}
                    anchor="right"
                    size="md"
                    variant="plain"
                    slotProps={{
                        content: {
                            sx: {
                                bgcolor: 'transparent',
                                p: {md: 3, sm: 0},
                                boxShadow: 'none',
                                minWidth: "700px"
                            },
                        },
                    }}
            >
                <Sheet
                    sx={{
                        borderRadius: 'xl',
                        p: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                        height: '100%',
                        overflow: 'auto',
                    }}
                >
                    <Stack direction="column" spacing={2} p={3}>
                        <Typography component="h2" level="h4" sx={{fontWeight: "lg", mb: 1}}>
                            Modifica utente
                        </Typography>

                        <FormControl>
                            <FormLabel>Nome</FormLabel>
                            <Input
                                type="text"
                                value={data.firstName}
                                onChange={(e) => handleChange("firstName", e.target.value)}
                                placeholder="Mario"
                            />
                        </FormControl>

                        <FormControl>
                            <FormLabel>Cognome</FormLabel>
                            <Input
                                type="text"
                                value={data.lastName}
                                onChange={(e) => handleChange("lastName", e.target.value)}
                                placeholder="Rossi"
                            />
                        </FormControl>

                        <FormControl>
                            <FormLabel>Email</FormLabel>
                            <Input
                                type="email"
                                value={data.email}
                                onChange={() => {}}
                                placeholder="name@email.com"
                                readOnly
                                endDecorator={<Lock/>}
                            />
                        </FormControl>

                        <FormControl>
                            <Checkbox
                                checked={Boolean(data.terms)}
                                onChange={() => handleChange("terms", !Boolean(data.terms))}
                                label="Termini e condizioni"
                            />
                        </FormControl>

                        <FormControl>
                            <Checkbox
                                checked={Boolean(data.marketing)}
                                onChange={() => handleChange("marketing", !Boolean(data.marketing))}
                                label="Consenso marketing"
                            />
                        </FormControl>

                        <FormControl>
                            <Checkbox
                                checked={Boolean(data.isAdmin)}
                                onChange={() => handleChange("isAdmin", !Boolean(data.isAdmin))}
                                label="Permessi amministratore"
                            />
                        </FormControl>

                        <Stack direction="row" spacing={2}>
                            <Button fullWidth
                                    variant="outlined"
                                    color="neutral"
                                    onClick={() => cancelModal()}
                            >
                                Annulla
                            </Button>
                            <Button
                                fullWidth
                                disabled={
                                    data.firstName?.trim().length === 0 ||
                                    data.lastName?.trim().length === 0 ||
                                    !hasChanges(defaultData, data)
                                }
                                onClick={handleSubmit}
                            >
                                Modifica
                            </Button>
                        </Stack>

                    </Stack>
                </Sheet>
            </Drawer>
        </Fragment>
    )

    function cancelModal() {
        setModalEdit(false)
        setData(defaultData)
    }
}
